"use client";

import { type ReactNode, useEffect, useState } from "react";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "../../hover-card";
import { defaultAvatarRenderer } from "../renderers";
import type { AvatarRenderer, BaseInteraction, FacepileSize } from "../types";

interface WebAdapterProps<T extends BaseInteraction> {
	item: T;
	size: FacepileSize;
	renderAvatar?: AvatarRenderer<T>;
	renderInteraction?: (item: T) => ReactNode;
	href?: string;
	enableHoverCard?: boolean;
	enableLink?: boolean;
	modalAware?: boolean;
	onItemClick?: (item: T) => void;
}

/**
 * Web-specific adapter that adds hover cards and link navigation
 */
export function WebAdapter<T extends BaseInteraction>({
	item,
	size,
	renderAvatar = defaultAvatarRenderer,
	renderInteraction,
	href,
	enableHoverCard = true,
	enableLink = true,
	modalAware = false,
	onItemClick,
}: WebAdapterProps<T>) {
	// Track modal state to prevent hover cards from opening when modals are active
	const [isModalOpen, setIsModalOpen] = useState(false);

	useEffect(() => {
		if (!modalAware) return;

		const checkModalState = () => {
			const modalExists = !!document.querySelector(
				'[data-state="open"][role="dialog"]',
			);
			setIsModalOpen(modalExists);
		};

		// Check immediately
		checkModalState();

		// Set up observer for modal state changes
		const observer = new MutationObserver(checkModalState);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["data-state"],
		});

		return () => observer.disconnect();
	}, [modalAware]);

	// Base avatar element
	const avatarElement = renderAvatar(item, size);

	// Wrap with link if enabled - elegant and minimal
	const linkWrappedElement =
		enableLink && href ? (
			<a
				href={href}
				onClick={(e) => {
					// Force the hover card to close immediately on click
					if (document.activeElement instanceof HTMLElement) {
						document.activeElement.blur();
					}
					onItemClick?.(item);
				}}
				className="inline-block"
			>
				{avatarElement}
			</a>
		) : (
			<div
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onItemClick?.(item);
				}}
				className="inline-block cursor-pointer"
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						e.stopPropagation();
						onItemClick?.(item);
					}
				}}
			>
				{avatarElement}
			</div>
		);

	// Wrap with hover card if enabled
	if (enableHoverCard && renderInteraction) {
		// When modalAware is true, we allow hover cards in modals but keep them uncontrolled
		// This prevents the controlled/uncontrolled switching warning
		return (
			<HoverCard openDelay={300} closeDelay={150}>
				<HoverCardTrigger asChild>{linkWrappedElement}</HoverCardTrigger>
				<HoverCardContent
					side="top"
					align="center"
					className="w-auto cursor-default p-3"
				>
					{renderInteraction(item)}
				</HoverCardContent>
			</HoverCard>
		);
	}

	return linkWrappedElement;
}
